<script setup lang="ts">
import Label from '@ui/components/form/Label.vue'
import Icon from '@ui/components/icon/Index.vue'

import { XMarkIcon } from '@heroicons/vue/24/solid'

import { usePadding } from '@ui/composables/form/input/usePadding'
import { useVariant } from '@ui/composables/form/useVariant'

import type { BorderRadius, Color, Size, Variant } from '@ui/types/form/input'

type ModelValue = string | number | undefined | null

const emit = defineEmits<{
  (event: 'update:modelValue', value: ModelValue): void
}>()

const props = withDefaults(
  defineProps<{
    id?: string
    autocomplete?: string
    backgroundColor?: Color
    borderColor?: Color
    borderColorOverride?: string
    clearable?: boolean
    disabled?: boolean
    inline?: boolean
    modelValue?: ModelValue
    padding?: Size
    rounded?: BorderRadius
    size?: Size
    variant?: Variant
  }>(),
  {
    id: undefined,
    autocomplete: 'off',
    backgroundColor: 'white',
    borderColor: 'default',
    borderColorOverride: '',
    clearable: false,
    disabled: false,
    inline: false,
    modelValue: '',
    padding: 'md',
    rounded: 'md',
    size: 'md',
    variant: 'default'
  }
)

const {
  autocomplete,
  //borderColor,
  borderColorOverride,
  clearable,
  disabled,
  inline,
  modelValue,
  padding,
  rounded,
  size,
  variant
} = toRefs(props)

const inputValue = computed<ModelValue>({
  get: () => modelValue.value,
  set: (value: ModelValue) => emit('update:modelValue', value)
})

const inputRef = ref<HTMLInputElement | null>(null)
const id = props.id ? props.id : `input-${useId()}`

const clear = () => {
  inputValue.value = ''
}

defineExpose({
  blur: () => inputRef.value?.blur(),
  focus: () => inputRef.value?.focus()
})
</script>

<template>
  <div class="flex flex-col">
    <Label
      v-show="$slots.label"
      :for="id"
    >
      <slot name="label" />
    </Label>
    <div
      class="relative"
      :tabindex="disabled ? 0 : -1"
    >
      <input
        :id="id"
        ref="inputRef"
        v-model="inputValue"
        v-bind="$attrs"
        :disabled="disabled"
        :class="[
          'border dark:text-white dark:placeholder:text-gray-300 dark:focus:placeholder:text-white',
          { 'opacity-75 pointer-events-none': disabled, 'flex w-full': !inline, 'inline-flex': inline },
          useTailwindClasses([
            useBackgroundColor(backgroundColor),
            useBorderRadius(rounded),
            usePadding(padding ? padding : size),
            useTextSize(size),
            useVariant(variant)
          ]),
          { [borderColorOverride]: borderColorOverride }
        ]"
        :autocomplete="autocomplete"
      />
      <div
        v-if="clearable && inputValue"
        class="absolute rounded-r inset-y-0 right-0 flex items-center bg-transparent text-gray-500 space-x-1 px-3"
      >
        <button
          class="bg-white hover:text-black focus:text-black"
          @click.stop="clear"
        >
          <Icon :icon="XMarkIcon" />
        </button>
      </div>
    </div>
  </div>
</template>
